/**
 * Copia del paso 1 (ligar el DISPOSITIVO al club con el código de club),
 * compartida por las dos superficies para que digan LO MISMO: la tablet del
 * club (`/tablet`, este repo) y el visor (`vr-app/src/ui/KitLogin.tsx`, que
 * importa las mismas cadenas desde su propio espejo — los dos bundles son
 * independientes, así que el contrato es "si cambia acá, cambia allá").
 *
 * Decisión de mg (2026-09-07): *"Kit debería pedir como manera de autenticar
 * con el club, tanto en tablet como en VR, siempre que arrancan o haga
 * falta"*. Lo único que cambia entre superficies es CÓMO se llama el
 * dispositivo; el resto del flujo (paso 1 código → paso 2 PIN, desligar,
 * motivos de re-pedido) es idéntico.
 */

export type KitDeviceKind = 'tablet' | 'headset' | 'device'

export interface KitDeviceCopy {
  /** Etiqueta chica y siempre visible: qué dispositivo es este. */
  chrome: string
  /** Título del paso 1. */
  title: string
  /** Qué se espera del staff en el paso 1. */
  description: string
  /** Botón de desligado (staff), en la pantalla de PIN. */
  unbind: string
}

export const DEVICE_BINDING_COPY: Record<KitDeviceKind, KitDeviceCopy> = {
  tablet: {
    chrome: 'Tablet del club',
    title: 'Vinculá esta tablet con el club',
    description:
      'Ingresá el código de club. Se pide una sola vez: esta tablet queda ligada a ese club y después cada jugador entra con su PIN.',
    unbind: 'Desligar esta tablet',
  },
  headset: {
    chrome: 'Visor del club',
    title: 'Vinculá este visor con el club',
    description:
      'Ingresá el código de club. Se pide una sola vez: este visor queda ligado a ese club y después cada jugador entra con su PIN.',
    unbind: 'Desligar este visor',
  },
  device: {
    chrome: 'Dispositivo del club',
    title: 'Vinculá este dispositivo con el club',
    description:
      'Ingresá el código de club. Se pide una sola vez: este dispositivo queda ligado a ese club y después cada jugador entra con su PIN.',
    unbind: 'Desligar este dispositivo',
  },
}

/**
 * Por qué se volvió a pedir el código en un dispositivo que YA estaba ligado.
 * Son los dos motivos que el backend sabe distinguir en `/auth/kit/verify`:
 * 404 (el kit no existe o fue revocado) y `club.service_active === false`.
 * Un error de red NO es motivo: el dispositivo sigue con su binding y el
 * jugador entra igual (ver `useKitBinding.ts`).
 */
export const KIT_REVOKED_REASON =
  'Este código de club ya no está activo. Pedile al staff el código vigente.'

export function kitSuspendedReason(clubName: string | null): string {
  const club = clubName ? ` de ${clubName}` : ''
  return `El servicio${club} está pausado. Hablá con el club: el dispositivo vuelve a funcionar solo cuando se reactive.`
}
